import { Warning } from '@mui/icons-material';
import { IDetailCellRendererParams } from 'ag-grid-community';
import { useMemo } from 'react';
import { IconCell } from 'src/components/molecules/IconCell/IconCell';
import { createDynamicMonthColumns } from 'src/components/organisms/DataTable/columns/utils';
import { ScopeEnum } from 'src/domain';
import { ProjectedOptionCountRow } from 'src/domain/table/projected-option-count.row';
import { useScope } from 'src/hooks';
import { useProjectedOptionCountHeatMapRows } from 'src/hooks/projected-option-count/table/useProjectedOptionCountHeatMapRows';
import {
    ProjectedOptionCountInventory,
    useProjectedOptionCountInventory,
} from 'src/hooks/projected-option-count/useProjectedOptionCountInventory';
import { useMonthKeys } from 'src/hooks/useMonthKeys';
import { HeatMapTypeEnum } from 'src/mapping/projected-option-count.mapping';
import { formatNumber, parseNumber } from 'src/utils/formatNumber';
import { createRangeRemapper } from 'src/utils/rangeRemapper';
import { categoryColumn, getHeatMapColor } from './columns';

export interface Props {
    type: HeatMapTypeEnum;
}

const createMonthlyColumnCellRendererParams =
    (inventory: ProjectedOptionCountInventory[], scope: ScopeEnum, type: HeatMapTypeEnum) =>
    (params: IDetailCellRendererParams) => {
        if (params.data.category === 'Total') {
            return createTotalRowCellRendererParams(params, inventory, scope, type);
        }
        return createRowCellRendererParams(params, inventory, scope, type);
    };

const createTotalRowCellRendererParams = (
    params: IDetailCellRendererParams,
    inventory: ProjectedOptionCountInventory[],
    scope: ScopeEnum,
    type: HeatMapTypeEnum
) => {
    const totalOptions = inventory?.reduce((sum, row) => (row.enabled ? sum + (row.options || 0) : sum), 0) ?? 0;
    const isInventoryGroupedType = type === HeatMapTypeEnum.Furniture || type === HeatMapTypeEnum.Zone;
    const show = scope === ScopeEnum.STORE && isInventoryGroupedType && totalOptions < Number(params.valueFormatted);
    const itemLabel = type === HeatMapTypeEnum.Zone ? 'furniture zone' : 'furniture';

    return {
        icon: <Warning color="warning" />,
        showIcon: show,
        tooltip: show ? `Projected options exceed total options available by ${itemLabel} from Store Inventory` : '',
    };
};

const createRowCellRendererParams = (
    params: IDetailCellRendererParams,
    inventory: ProjectedOptionCountInventory[],
    scope: ScopeEnum,
    type: HeatMapTypeEnum
) => {
    const item = inventory.find((item) => item.name === params.data.category);
    const isInventoryGroupedType = type === HeatMapTypeEnum.Furniture || type === HeatMapTypeEnum.Zone;
    const show =
        scope === ScopeEnum.STORE &&
        isInventoryGroupedType &&
        item &&
        item.enabled &&
        item.options < Number(params.valueFormatted);
    const itemLabel = type === HeatMapTypeEnum.Zone ? 'furniture zone' : 'furniture';

    return {
        icon: <Warning color="warning" />,
        showIcon: show,
        tooltip: show ? `The ${itemLabel} has more options than in Store Inventory` : '',
    };
};

const createCategoryColumnCellRendererParams =
    (inventory: ProjectedOptionCountInventory[], scope: ScopeEnum, type: HeatMapTypeEnum) =>
    (params: IDetailCellRendererParams) => {
        const item = inventory.find((item) => item.name === params.value);
        const isInventoryGroupedType = type === HeatMapTypeEnum.Furniture || type === HeatMapTypeEnum.Zone;
        const show =
            scope === ScopeEnum.STORE && isInventoryGroupedType && params.data.minValue > 0 && item && !item?.enabled;
        const itemLabel = type === HeatMapTypeEnum.Zone ? 'furniture zone' : 'furniture';

        return {
            icon: <Warning color="warning" fontSize="small" />,
            showIcon: show,
            tooltip: show ? `The ${itemLabel} is not enabled in Store Inventory` : '',
        };
    };

export const useProjectedOptionCountColumns = ({ type }: Props) => {
    const scope = useScope();
    const monthKeys = useMonthKeys();
    const inventory = useProjectedOptionCountInventory(type);

    const { data: rows, loading } = useProjectedOptionCountHeatMapRows(type);

    const monthlyColumns = useMemo(() => {
        const baseColumns = createDynamicMonthColumns<ProjectedOptionCountRow>(
            monthKeys.map((monthKey) => ({ monthKey })),
            {
                fieldPrefix: 'columns',
                editable: false,
                valueFormatter({ value }) {
                    return formatNumber(parseNumber(value), 0);
                },
                cellStyle({ value, data }) {
                    if (!data) {
                        return;
                    }

                    const valueAsNumber = parseNumber(value);
                    const remap = createRangeRemapper([
                        data.minValue === data.maxValue ? data.maxValue - 1 : data.minValue,
                        data.maxValue,
                    ]);

                    const backgroundColor = valueAsNumber === 0 ? '#fff' : getHeatMapColor(remap(valueAsNumber));
                    const fontWeight = data.category === 'Total' ? 'bold' : 'normal';

                    return {
                        backgroundColor,
                        fontWeight,
                    };
                },
                cellRenderer: IconCell,
            }
        );

        return baseColumns.map((column) => ({
            ...column,
            cellRendererParams: createMonthlyColumnCellRendererParams(inventory, scope || ScopeEnum.GLOBAL, type),
        }));
    }, [monthKeys, inventory, scope, type]);

    const columns = useMemo(() => {
        let headerName = 'Furniture zone';
        if (type === HeatMapTypeEnum.Category) {
            headerName = 'Category';
        } else if (type === HeatMapTypeEnum.Furniture) {
            headerName = 'Furniture';
        }

        return [
            {
                ...categoryColumn,
                headerName,
                cellRenderer: IconCell,
                cellRendererParams: createCategoryColumnCellRendererParams(inventory, scope || ScopeEnum.GLOBAL, type),
            },
            ...monthlyColumns,
        ];
    }, [monthlyColumns, type, inventory, scope]);

    return { data: rows, columns, loading };
};
